/**
 * The files a New application card holds until Create, by its choice's ref, and their upload to
 * each application the call created. Nothing is stored before the create: the files stay in this
 * tab (a reload drops them), and the application PDF is only read, by the portal's parse, to fill
 * Details. Once the call ran, every held file goes to each created application through the
 * portal's upload-statements, once per application, so intake runs as for the portal's create.
 * Once every application has them the card keeps only their count; a card declined, expired or
 * superseded lets them go (`forgetHeld`).
 */
import { useSyncExternalStore } from "react";

import { borrowerService } from "@/services/borrowers";
import type { CardApplicationValues } from "@/types/extraction";

/** How the dropped application PDF's parse went. */
export type ReadState = "reading" | "read" | "failed";
export type UploadState = "uploading" | "done" | "failed";

export interface HeldFile {
  id: number;
  file: File;
  /** The one the card read to fill Details (the application), or another document. */
  application: boolean;
  read?: ReadState;
}

export interface Held {
  files: HeldFile[];
  /** The application PDF's values and the server's signature of them, once read. */
  pdf: CardApplicationValues | null;
  /** By created application id. */
  uploads: Record<number, UploadState>;
  /** How many files went to every application, once they did (and `files` let go). */
  attached: number;
}

const EMPTY: Held = { files: [], pdf: null, uploads: {}, attached: 0 };
const held = new Map<string, Held>();
const listeners = new Set<() => void>();
let nextId = 1;

function update(ref: string, change: (current: Held) => Held) {
  held.set(ref, change(held.get(ref) ?? EMPTY));
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** What the card `ref` holds; the same object until it changes. */
export function useHeld(ref: string | null): Held {
  return useSyncExternalStore(
    subscribe,
    () => (ref ? (held.get(ref) ?? EMPTY) : EMPTY),
    () => EMPTY
  );
}

export function holdFiles(ref: string, files: File[], application = false): HeldFile[] {
  const added = files.map((file) => ({ id: nextId++, file, application }));
  update(ref, (current) => ({ ...current, files: [...current.files, ...added] }));
  return added;
}

export function dropFile(ref: string, id: number) {
  update(ref, (current) => {
    const gone = current.files.find((file) => file.id === id);
    return {
      ...current,
      files: current.files.filter((file) => file.id !== id),
      // The application's values go with it: what it filled stays on the card as typed.
      pdf: gone?.application ? null : current.pdf,
    };
  });
}

/** Whether the file `id` is still held: one dropped (or let go) takes no read. */
export function setRead(
  ref: string,
  id: number,
  read: ReadState,
  pdf?: CardApplicationValues
): boolean {
  if (!held.get(ref)?.files.some((file) => file.id === id)) return false;
  update(ref, (current) => ({
    ...current,
    files: current.files.map((file) => (file.id === id ? { ...file, read } : file)),
    pdf: pdf ?? current.pdf,
  }));
  return true;
}

function setUpload(ref: string, applicationId: number, state: UploadState) {
  update(ref, (current) => ({
    ...current,
    uploads: { ...current.uploads, [applicationId]: state },
  }));
}

/**
 * Upload what `ref` holds to the application `applicationId`, one of `applications` (every one the
 * call created): skipped once it is uploading or done, so a card drawn twice (or in two tabs of one
 * page) uploads once, and once it failed unless the user tries it `again`. Once all of them have
 * the files, only their count stays.
 */
export async function uploadTo(
  ref: string,
  applicationId: number,
  applications: number[],
  again = false
): Promise<void> {
  const current = held.get(ref);
  if (!current?.files.length) return;
  const state = current.uploads[applicationId];
  if (state === "uploading" || state === "done" || (state === "failed" && !again)) return;
  setUpload(ref, applicationId, "uploading");
  try {
    await borrowerService.uploadStatements(
      applicationId,
      current.files.map((file) => file.file)
    );
    setUpload(ref, applicationId, "done");
    update(ref, (after) =>
      applications.every((id) => after.uploads[id] === "done")
        ? { ...after, files: [], attached: after.files.length }
        : after
    );
  } catch {
    setUpload(ref, applicationId, "failed");
  }
}

/** Let go of what the card `ref` holds: it will create nothing. */
export function forgetHeld(ref: string) {
  if (!held.has(ref)) return;
  held.delete(ref);
  listeners.forEach((listener) => listener());
}

/** For tests: forget every card's files. */
export function resetHeld() {
  held.clear();
  listeners.forEach((listener) => listener());
}
